import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  AlertTriangle,
  CheckCircle2,
  BookOpen,
  Award,
  Recycle,
  Layers,
  Boxes,
  Sparkles,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city, products = [] }) => {
  const livres = products.find((p) => p.slug === "carton-a-livres");

  return (
    <>
      {/* ======= SECTION 2 — Livraison sur mesure ======= */}
      <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison sur mesure
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Votre livraison de cartons à {city.name}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Dans une commune préservée comme {city.name},
              l&apos;acheminement de vos fournitures exige une flexibilité
              totale et une parfaite connaissance des contraintes locales.
              Nous avons banni les méthodes de transport traditionnelles
              pour vous offrir un service de distribution d&apos;une
              efficacité absolue.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                title: "Acheminement en circuit court propriétaire",
                body: "Nous refusons catégoriquement de confier vos commandes à des plateformes de messagerie ou des transporteurs tiers. C'est notre propre flotte de véhicules franciliens qui prend en charge vos fournitures. Vous bénéficiez d'une ponctualité chirurgicale, d'une manipulation soignée et d'une parfaite adaptation aux accès résidentiels de la ville.",
                Icon: Truck,
              },
              {
                title: "Garantie d'hygrométrie et de géométrie",
                body: "Le carton est un matériau vivant, sensible aux variations climatiques. Voyageant au sec dans nos fourgons hermétiques et capitonnés, vos modules sont protégés de la poussière routière et de l'humidité. Vous réceptionnez un matériel à la rigidité intacte, immédiatement opérationnel pour l'assemblage.",
                Icon: ShieldCheck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
              >
                <div className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                    {title}
                  </h3>
                </div>
                <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======= SECTION 3 — Physique du déménagement : carton livres (comparaison danger vs solution) ======= */}
      <section className="py-14 md:py-20 bg-white">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFE9D6] border border-[#FF7A00]/30 text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00] mb-4">
              <BookOpen className="h-3.5 w-3.5" strokeWidth={2.6} />
              Physique du déménagement
            </div>
            <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Pourquoi le carton « petit format » sauvera vos livres
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              C&apos;est l&apos;un des pièges les plus fréquents lors des
              préparatifs&nbsp;: vouloir ranger ses encyclopédies, romans
              et dossiers administratifs dans de grands cartons standards
              pour aller plus vite. C&apos;est scientifiquement la
              meilleure façon de provoquer un accident logistique.
            </p>
          </div>

          {/* Comparison: Danger vs Solution */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* DANGER card */}
            <article className="relative bg-[#fff5e8] border-2 border-[#FF7A00]/40 rounded-2xl p-6 md:p-7 overflow-hidden">
              <div
                aria-hidden="true"
                className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-[#FF7A00]/15 blur-2xl"
              />
              <div className="relative">
                <div className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#FF7A00] text-white">
                    <AlertTriangle className="h-5 w-5" strokeWidth={2.6} />
                  </span>
                  <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00]">
                    L&apos;ennemi juré&nbsp;: la densité
                  </div>
                </div>
                <div className="mt-5">
                  <div className="font-display font-extrabold text-4xl md:text-5xl tracking-tight text-[#0f1d2b]">
                    40 à 50&nbsp;kg
                  </div>
                  <div className="mt-1 text-[11px] uppercase tracking-[0.16em] font-bold text-[#FF7A00]">
                    Poids d&apos;un grand carton plein de livres
                  </div>
                </div>
                <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
                  Le papier est l&apos;un des matériaux les plus lourds à
                  transporter. Un grand carton entièrement rempli de
                  livres peut facilement atteindre 40 à 50&nbsp;kg.
                  Résultat&nbsp;? Le fond risque de céder sous la pression
                  lors du portage, et vous risquez de vous blesser
                  gravement au dos.
                </p>
              </div>
            </article>

            {/* SOLUTION card */}
            <article className="relative bg-[#EEF3F7] border-2 border-[#237cb5]/40 rounded-2xl p-6 md:p-7 overflow-hidden">
              <div
                aria-hidden="true"
                className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-[#237cb5]/15 blur-2xl"
              />
              <div className="relative">
                <div className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                    <CheckCircle2 className="h-5 w-5" strokeWidth={2.6} />
                  </span>
                  <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5]">
                    La solution&nbsp;: volume maîtrisé
                  </div>
                </div>
                <div className="mt-5">
                  <div className="font-display font-extrabold text-4xl md:text-5xl tracking-tight text-[#0f1d2b]">
                    15 à 20&nbsp;kg
                  </div>
                  <div className="mt-1 text-[11px] uppercase tracking-[0.16em] font-bold text-[#237cb5]">
                    Charge ergonomique du carton à livres
                  </div>
                </div>
                <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
                  Notre carton « petit format » est spécifiquement
                  dimensionné pour plafonner le poids total du colis à une
                  charge ergonomique. Sa structure renforcée empêche toute
                  déformation latérale. Vous pouvez ainsi empiler vos
                  ouvrages les plus denses en toute sécurité, avec des
                  manipulations fluides et sans effort.
                </p>
              </div>
            </article>
          </div>

          {/* Featured product CTA strip */}
          {livres && (
            <Link
              to="/produit/carton-a-livres"
              className="group mt-6 flex items-center justify-between bg-gradient-to-br from-[#0f1d2b] to-[#1a2d3f] hover:shadow-[0_28px_60px_-26px_rgba(15,29,43,0.5)] rounded-2xl px-6 md:px-8 py-5 md:py-6 text-white transition-all"
              data-testid="city-sceaux-cta-livres"
            >
              <div className="flex items-center gap-4 md:gap-5">
                <span className="flex items-center justify-center h-14 w-14 rounded-2xl bg-[#FF7A00] text-white shrink-0">
                  <BookOpen className="h-7 w-7" strokeWidth={2.4} />
                </span>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00]">
                    La référence à adopter
                  </div>
                  <h3 className="mt-1 font-display font-extrabold text-xl text-white leading-tight">
                    {livres.name}
                  </h3>
                  <div className="mt-1 text-[13px] text-white/70">
                    {livres.dimensions || "Format compact renforcé"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <div className="text-right">
                  <div className="font-display font-extrabold text-2xl text-white">
                    {Number(livres.price).toFixed(2).replace(".", ",")} €
                  </div>
                  <div className="text-[12px] text-white/60">par carton</div>
                </div>
                <ArrowRight
                  className="h-5 w-5 text-[#FF7A00] group-hover:translate-x-1 transition-transform"
                  strokeWidth={2.6}
                />
              </div>
            </Link>
          )}
        </div>
      </section>

      {/* ======= CTA Big Déménagement (au milieu) ======= */}
      <section className="py-12 bg-white">
        <div className="container-cdm">
          <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
        </div>
      </section>

      {/* ======= SECTION 4 — Pourquoi adopter : 3 critères certifiés ======= */}
      <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#cfdbe6] text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5] mb-4">
              <Sparkles className="h-3.5 w-3.5" strokeWidth={2.6} />
              Critères de fabrication
            </div>
            <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Pourquoi adopter nos fournitures&nbsp;?
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Faire le choix de notre catalogue, c&apos;est opter pour une
              rigueur industrielle pensée pour éliminer la moindre source
              de stress grâce à des critères de fabrication stricts.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                kicker: "Certifié",
                title: "Résistance au gerbage",
                body: "Nos cartons possèdent une épaisseur de double cannelure supérieure aux standards du commerce. Ils sont conçus pour supporter un empilement vertical intensif à l'intérieur du camion, optimisant chaque centimètre cube disponible sans écraser les couches inférieures.",
                Icon: Award,
              },
              {
                kicker: "Éco-conception",
                title: "Circulaire et biodégradable",
                body: "Fabriqués à partir de fibres végétales recyclées et de colles naturelles, nos contenants sont 100% biodégradables. Ils sont pensés pour être hautement réutilisables d'un projet à un autre avant d'intégrer les filières de tri standard.",
                Icon: Recycle,
              },
              {
                kicker: "Géométrie",
                title: "Formats standardisés",
                body: "L'intégralité de nos boîtes et cartons s'emboîte selon des lignes géométriques précises. Cela permet de créer des blocs stables et solidarisés dans le véhicule de transport, évitant les glissements et les chutes lors des virages ou des freinages.",
                Icon: Layers,
              },
            ].map(({ kicker, title, body, Icon }) => (
              <article
                key={title}
                className="bg-white rounded-2xl border border-[#cfdbe6] p-6 md:p-7 flex flex-col"
              >
                <div className="flex items-start gap-3">
                  <span className="flex items-center justify-center h-12 w-12 rounded-full bg-[#237cb5] text-white shrink-0">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <div className="flex-1">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#EEF3F7] text-[10.5px] uppercase tracking-[0.16em] font-bold text-[#237cb5]">
                      <CheckCircle2
                        className="h-3 w-3"
                        strokeWidth={2.6}
                      />
                      {kicker}
                    </div>
                    <h3 className="mt-2 font-display font-extrabold text-[16.5px] text-[#0f1d2b] leading-tight">
                      {title}
                    </h3>
                  </div>
                </div>
                <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </article>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              to="/cartons-demenagement"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-sceaux-cta-shop"
            >
              <Boxes className="h-4 w-4" strokeWidth={2.6} />
              Voir tous les cartons
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
            <Link
              to="/packs-cartons"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
              data-testid="city-sceaux-cta-packs"
            >
              Voir les kits complets
            </Link>
          </div>
        </div>
      </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-sceaux-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
    </>
  );
};

export default { Sections };
